/**
 * Google Sign-In button.
 *
 * Loads the Google Identity Services (GIS) client once, renders Google's
 * official "Sign in with Google" button, and forwards the returned ID token
 * (credential) to the parent via `onCredential`.
 *
 * Additive by design: if `VITE_GOOGLE_CLIENT_ID` is not configured, the
 * component renders nothing, so the surrounding UI is unaffected.
 *
 * @module GoogleSignInButton
 */

import { useEffect, useRef } from "react";
import { logger } from "@/lib/logger";

interface GoogleCredentialResponse {
  credential?: string;
}

interface GoogleAccountsId {
  initialize: (config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
  }) => void;
  renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleAccountsId } };
  }
}

const GSI_SRC = "https://accounts.google.com/gsi/client";
const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

/**
 * Load the GIS client script exactly once (shared across mounts).
 */
function loadGsiScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GSI_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load Google Sign-In")),
      );
      return;
    }
    const script = document.createElement("script");
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google Sign-In"));
    document.head.appendChild(script);
  });
}

interface GoogleSignInButtonProps {
  /** Called with the Google ID token (credential) once the user authenticates. */
  onCredential: (idToken: string) => void;
}

export function GoogleSignInButton({
  onCredential,
}: Readonly<GoogleSignInButtonProps>) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!CLIENT_ID || !containerRef.current) return;

    let cancelled = false;
    loadGsiScript()
      .then(() => {
        const gid = window.google?.accounts?.id;
        if (cancelled || !gid || !containerRef.current) return;
        gid.initialize({
          client_id: CLIENT_ID,
          callback: (resp) => {
            if (resp?.credential) onCredential(resp.credential);
          },
        });
        const availableWidth =
          containerRef.current.getBoundingClientRect().width;
        gid.renderButton(containerRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "rectangular",
          logo_alignment: "center",
          width: Math.min(320, Math.max(200, Math.floor(availableWidth))),
        });
      })
      .catch((error) => {
        // Network/CSP failure: the button simply won't render; email/password
        // sign-in remains fully functional.
        logger.debug("Google Sign-In unavailable", error);
      });

    return () => {
      cancelled = true;
    };
  }, [onCredential]);

  // Feature is off when no Client ID is configured -> zero UI impact.
  if (!CLIENT_ID) return null;

  return (
    <div
      ref={containerRef}
      className="flex w-full min-w-0 justify-center overflow-hidden"
    />
  );
}
